import React, { useEffect } from 'react';
import './Software.css';
import heroBackground from '../Assets/it.jpg';
import { Link } from 'react-router-dom';

// Import all icon images
import javaImage from '../Assets/java.jpg';
import reactImage from '../Assets/r.jpg';
import reactNativeImage from '../Assets/rn.jpeg';
import databaseImage from '../Assets/db.jpg';
import mobileUiImage from '../Assets/ui and ux.jpg';
import codeReuseImage from '../Assets/Code Reusability.jpg';
import performanceImage from '../Assets/high.jpeg';
import scalabilityImage from '../Assets/scale.jpeg';

const Software = () => {
  const [activeTab, setActiveTab] = React.useState('web');

  useEffect(() => {
    const elements = document.querySelectorAll('.animate');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
            setTimeout(() => {
              entry.target.classList.add('animation-complete');
            }, 1200); // Match animation duration
          }
        });
      },
      { threshold: 0.2 }
    );

    elements.forEach((element) => observer.observe(element));

    return () => {
      elements.forEach((element) => observer.unobserve(element));
    };
  }, []);

  return (
    <div className="it-tech-page">
      {/* Hero Section with Background Image */}
      <header className="hero animate" style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${heroBackground})` }}>
        <div className="container">
          <div className="hero-content">
            <h1 className="slide-in">Innovative IT Solutions</h1>
            <p className="subtitle fade-in">
              Building high-performance applications with <span className="highlight">Java</span>,{' '}
              <span className="highlight">React.js</span>, and <span className="highlight">React Native</span>
            </p>
            <div className="cta-buttons">
              <a href="#technologies" className="btn primary pulse">
                Explore Our Tech
              </a>
            </div>
          </div>
          <div className="hero-image">
            <div className="tech-bubbles">
              <div className="bubble java">Java</div>
              <div className="bubble react">React.js</div>
              <div className="bubble react-native">React Native</div>
              <div className="bubble spring">Spring</div>
            </div>
          </div>
        </div>
      </header>

      {/* Technologies Section */}
      <section id="technologies" className="technologies-section">
        <div className="container">
          <h2 className="slide-up animate">Our Technology Stack</h2>
          <p className="section-description fade-in animate">
            We combine robust backend with modern frontend for full-stack solutions
          </p>

          <div className="tech-tabs">
            <button
              className={`tab-btn ${activeTab === 'web' ? 'active' : ''}`}
              onClick={() => setActiveTab('web')}
            >
              Web Development
              <span className="underline"></span>
            </button>
            <button
              className={`tab-btn ${activeTab === 'mobile' ? 'active' : ''}`}
              onClick={() => setActiveTab('mobile')}
            >
              Mobile Development
              <span className="underline"></span>
            </button>
          </div>

          <div className="tech-content">
            {activeTab === 'web' ? (
              <div className="web-tech cards-container">
                <div className="tech-card animate">
                  <div className="tech-image-container">
                    <img src={javaImage} alt="Java Backend" className="tech-image" />
                  </div>
                  <div className="tech-content-wrapper">
                    <h3>Java Backend</h3>
                    <p>Enterprise-grade solutions with Spring Boot, Hibernate, and Microservices architecture.</p>
                    <div className="tech-features">
                      <span>Spring Boot</span>
                      <span>REST APIs</span>
                      <span>Microservices</span>
                    </div>
                  </div>
                </div>

                <div className="tech-card animate">
                  <div className="tech-image-container">
                    <img src={reactImage} alt="React Frontend" className="tech-image" />
                  </div>
                  <div className="tech-content-wrapper">
                    <h3>React Frontend</h3>
                    <p>Interactive UIs with React hooks, Context API, and modern CSS-in-JS solutions.</p>
                    <div className="tech-features">
                      <span>React Hooks</span>
                      <span>Redux</span>
                      <span>TypeScript</span>
                    </div>
                  </div>
                </div>

                <div className="tech-card animate">
                  <div className="tech-image-container">
                    <img src={databaseImage} alt="Database Solutions" className="tech-image" />
                  </div>
                  <div className="tech-content-wrapper">
                    <h3>Database Solutions</h3>
                    <p>Optimized data storage with PostgreSQL, MongoDB, and Redis caching.</p>
                    <div className="tech-features">
                      <span>PostgreSQL</span>
                      <span>MongoDB</span>
                      <span>Redis</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mobile-tech cards-container">
                <div className="tech-card animate">
                  <div className="tech-image-container">
                    <img src={javaImage} alt="Shared Java Backend" className="tech-image" />
                  </div>
                  <div className="tech-content-wrapper">
                    <h3>Shared Java Backend</h3>
                    <p>The same powerful Java backend serves both web and mobile applications.</p>
                    <div className="tech-features">
                      <span>Spring Security</span>
                      <span>JWT Auth</span>
                      <span>REST APIs</span>
                    </div>
                  </div>
                </div>

                <div className="tech-card animate">
                  <div className="tech-image-container">
                    <img src={reactNativeImage} alt="React Native" className="tech-image" />
                  </div>
                  <div className="tech-content-wrapper">
                    <h3>React Native</h3>
                    <p>Cross-platform mobile apps with native performance from a single codebase.</p>
                    <div className="tech-features">
                      <span>iOS & Android</span>
                      <span>Expo</span>
                      <span>Native Modules</span>
                    </div>
                  </div>
                </div>

                <div className="tech-card animate">
                  <div className="tech-image-container">
                    <img src={mobileUiImage} alt="Mobile UI/UX" className="tech-image" />
                  </div>
                  <div className="tech-content-wrapper">
                    <h3>Mobile UI/UX</h3>
                    <p>Beautiful, intuitive interfaces following platform-specific design guidelines.</p>
                    <div className="tech-features">
                      <span>Responsive</span>
                      <span>Animations</span>
                      <span>Dark Mode</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <h2 className="slide-up animate">Why Choose Our Stack?</h2>
          <div className="benefits-grid cards-container">
            <div className="benefit-card animate">
              <div className="benefit-image-container">
                <img src={codeReuseImage} alt="Code Reusability" className="benefit-image" />
              </div>
              <div className="benefit-content">
                <h3>Code Reusability</h3>
                <p>Share business logic between web and mobile apps with Java backend + React frontends.</p>
              </div>
            </div>

            <div className="benefit-card animate">
              <div className="benefit-image-container">
                <img src={performanceImage} alt="High Performance" className="benefit-image" />
              </div>
              <div className="benefit-content">
                <h3>High Performance</h3>
                <p>Java provides robust backend processing while React ensures smooth UI rendering.</p>
              </div>
            </div>

            <div className="benefit-card animate">
              <div className="benefit-image-container">
                <img src={scalabilityImage} alt="Scalability" className="benefit-image" />
              </div>
              <div className="benefit-content">
                <h3>Scalability</h3>
                <p>Microservices architecture allows your application to grow with your business needs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="slide-up animate">Ready to Build Your Project?</h2>
            <p className="fade-in animate">
              Let's discuss how we can create powerful web and mobile applications with our technology stack.
            </p>
            <Link to="/contact" className="btn primary pulse">
              Get in Touch 
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Software;